// src/screens/Tenants.js v1.9
import React, { useState, useEffect, useRef } from 'react';
import { getTenants, createTenant, updateTenant, deleteTenant, renewTenant, getSettings, buildReminderMessage } from '../database/db';
import { useAuth } from '../context/AuthContext';
import { format, differenceInDays } from 'date-fns';
import { Plus, Edit2, Trash2, RefreshCw, AlertTriangle, Search, Bell, Upload, Download } from 'lucide-react';

const fmtDate = iso => { try { return format(new Date(iso),'dd/MM/yyyy'); } catch { return '—'; } };
const isExpired = iso => !iso||new Date(iso)<new Date();
const daysLeft = iso => { if(!iso) return -999; return differenceInDays(new Date(iso),new Date()); };
const PKG_TYPES=[
  {value:'monthly-24hr',label:'🕐 Monthly 24hr'},
  {value:'monthly-night',label:'🌙 Monthly Night Only'},
  {value:'monthly-day',label:'☀️ Monthly Day Only'},
  {value:'weekly',label:'📅 Weekly'},
  {value:'biweekly',label:'📅 Bi-Weekly'}
];
const getPkgLabel = v => PKG_TYPES.find(p=>p.value===v)?.label||v||'Monthly';
const INIT={name:'',phone:'',plates:[''],packageType:'monthly-24hr',monthlyRate:'',weeklyRate:'',biweeklyRate:'',allowedStartTime:'22:00',allowedEndTime:'08:00',startDate:new Date().toISOString().split('T')[0],expiryDate:'',notes:''};

// CSV template columns
const CSV_HEADERS = ['name','phone','plates','packageType','monthlyRate','weeklyRate','biweeklyRate','expiryDate','allowedStartTime','allowedEndTime'];
const CSV_EXAMPLE = [
  'Ahmed Al Mansouri','+971501234567','28031;70046','monthly-24hr','500','','','31/12/2026','',''
];

export default function Tenants() {
  const { user } = useAuth();
  const [tenants, setTenants] = useState([]);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [tab, setTab] = useState('list'); // 'list' | 'import'
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(INIT);
  const [selected, setSelected] = useState(null);
  const [renewExpiryDate, setRenewExpiryDate] = useState('');
  const [renewPkgType, setRenewPkgType] = useState('');
  const [alert, setAlert] = useState(null);
  // Bulk import state
  const [csvRows, setCsvRows] = useState([]);
  const [importAlert, setImportAlert] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importDone, setImportDone] = useState(0);
  const fileRef = useRef();
  const settings = getSettings();

  const load = async() => {
    const t = await getTenants();
    setTenants(t||[]);
  };
  useEffect(()=>{load();},[]);

  const closeModal = () => { setModal(null); setSelected(null); setForm(INIT); setAlert(null); setRenewExpiryDate(''); setRenewPkgType(''); };

  const openAdd = () => { setForm(INIT); setModal('add'); };
  const openEdit = t => { setSelected(t); setForm({name:t.name,phone:t.phone||'',plates:t.plates||[''],packageType:t.packageType||'monthly-24hr',monthlyRate:t.monthlyRate||'',weeklyRate:t.weeklyRate||'',biweeklyRate:t.biweeklyRate||'',allowedStartTime:t.allowedStartTime||'22:00',allowedEndTime:t.allowedEndTime||'08:00',startDate:t.startDate||'',expiryDate:t.expiryDate?t.expiryDate.split('T')[0]:'',notes:''}); setModal('edit'); };
  const openRenew = t => { setSelected(t); setRenewExpiryDate(''); setRenewPkgType(''); setModal('renew'); };
  const openDelete = t => { setSelected(t); setModal('delete'); };

  const handleCreate = async() => {
    if(!form.name.trim()){setAlert({type:'danger',msg:'Name required.'});return;}
    await createTenant({...form,plates:form.plates.filter(p=>p.trim()),monthlyRate:parseFloat(form.monthlyRate)||null,weeklyRate:parseFloat(form.weeklyRate)||null,biweeklyRate:parseFloat(form.biweeklyRate)||null,expiryDate:form.expiryDate?new Date(form.expiryDate).toISOString():null});
    load(); closeModal();
  };
  const handleEdit = async() => {
    if(!form.name.trim()){setAlert({type:'danger',msg:'Name required.'});return;}
    await updateTenant(selected.id,{...selected,...form,plates:form.plates.filter(p=>p.trim()),monthlyRate:parseFloat(form.monthlyRate)||null,weeklyRate:parseFloat(form.weeklyRate)||null,biweeklyRate:parseFloat(form.biweeklyRate)||null,expiryDate:form.expiryDate?new Date(form.expiryDate).toISOString():null});
    load(); closeModal();
  };
  const handleDelete = async() => { await deleteTenant(selected.id); load(); closeModal(); };
  const handleRenew = async() => {
    if(!renewExpiryDate){setAlert({type:'danger',msg:'Select new expiry date.'});return;}
    if(new Date(renewExpiryDate)<=new Date()){setAlert({type:'danger',msg:'Expiry must be in future.'});return;}
    await renewTenant(selected.id,renewExpiryDate,user.id,renewPkgType||selected.packageType);
    load(); closeModal();
  };
  const sendReminder = t => {
    if(!t.phone){alert('No phone saved.');return;}
    window.open(`https://wa.me/${t.phone.replace(/\D/g,'')}?text=${buildReminderMessage(t)}`,'_blank');
  };

  // ── EXPORT TO EXCEL/CSV ───────────────────────────────────
  // Always exports ALL tenants, regardless of current search/filter on screen.
  const exportToExcel = () => {
    if(!tenants.length){ setImportAlert({type:'danger',msg:'No tenants to export.'}); setTab('import'); return; }
    const headers = ['Name','Phone','Plates','Package Type','Monthly Rate (AED)','Weekly Rate (AED)','Bi-Weekly Rate (AED)','Start Date','Expiry Date','Allowed From','Allowed Until','Status','Days Left'];
    const escapeCsv = v => {
      const s = (v===null||v===undefined) ? '' : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g,'""')}"` : s;
    };
    const rows = tenants.map(t=>{
      const exp = isExpired(t.expiryDate);
      const dl = daysLeft(t.expiryDate);
      const status = exp ? 'Expired' : (dl<=7 ? 'Expiring Soon' : 'Active');
      return [
        t.name||'',
        t.phone||'',
        (t.plates||[]).join('; '),
        getPkgLabel(t.packageType),
        t.monthlyRate||'',
        t.weeklyRate||'',
        t.biweeklyRate||'',
        t.startDate?fmtDate(t.startDate):'',
        t.expiryDate?fmtDate(t.expiryDate):'',
        t.allowedStartTime||'',
        t.allowedEndTime||'',
        status,
        t.expiryDate?dl:''
      ].map(escapeCsv).join(',');
    });
    const csv = [headers.map(escapeCsv).join(','), ...rows].join('\n');
    const blob = new Blob(['\uFEFF'+csv],{type:'text/csv;charset=utf-8;'}); // BOM for Excel
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    const today = new Date();
    const dstr = `${String(today.getDate()).padStart(2,'0')}-${String(today.getMonth()+1).padStart(2,'0')}-${today.getFullYear()}`;
    a.download = `tenants_${dstr}.csv`;
    a.click();
  };

  // ── BULK IMPORT ──────────────────────────────────────────
  const downloadTemplate = () => {
    const rows = [CSV_HEADERS.join(','), CSV_EXAMPLE.join(','),
      'Fatima Al Zaabi,+971509876543,1234,monthly-night,450,,,31/12/2026,22:00,08:00',
      'Mohammed Hassan,+971556543210,5678,weekly,,200,,15/06/2026,,'
    ];
    const blob = new Blob([rows.join('\n')],{type:'text/csv'});
    const a = document.createElement('a'); a.href=URL.createObjectURL(blob);
    a.download='tenant_import_template.csv'; a.click();
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0]; if(!file) return;
    setImportAlert(null); setCsvRows([]);
    const reader = new FileReader();
    reader.onload = ev => {
      const text = ev.target.result;
      const lines = text.split('\n').filter(l=>l.trim());
      if(lines.length<2){setImportAlert({type:'danger',msg:'File is empty or has no data rows.'});return;}
      const headers = lines[0].split(',').map(h=>h.trim().toLowerCase());
      const rows = [];
      for(let i=1;i<lines.length;i++){
        const vals = lines[i].split(',').map(v=>v.trim());
        const row = {};
        headers.forEach((h,idx)=>row[h]=vals[idx]||'');
        if(!row.name){continue;}
        rows.push({
          ...row,
          plates:(row.plates||'').split(';').filter(p=>p.trim()),
          valid: !!row.name && !!row.packagetype,
          error: !row.name?'Missing name':!row.packagetype?'Missing package type':''
        });
      }
      if(!rows.length){setImportAlert({type:'danger',msg:'No valid rows found.'});return;}
      setCsvRows(rows);
      setImportAlert({type:'success',msg:`${rows.length} row(s) ready to import. Review below then click Import All.`});
    };
    reader.readAsText(file);
    e.target.value='';
  };

  const handleImportAll = async() => {
    const valid = csvRows.filter(r=>r.valid);
    if(!valid.length){setImportAlert({type:'danger',msg:'No valid rows to import.'});return;}
    setImporting(true); setImportDone(0);
    let done=0;
    for(const row of valid){
      try{
        await createTenant({
          name:row.name,
          phone:row.phone||'',
          plates:row.plates||[],
          packageType:row.packagetype||row.packageType||'monthly-24hr',
          monthlyRate:parseFloat(row.monthlyrate||row.monthlyRate)||null,
          weeklyRate:parseFloat(row.weeklyrate||row.weeklyRate)||null,
          biweeklyRate:parseFloat(row.biweeklyrate||row.biweeklyRate)||null,
          expiryDate:row.expirydate||row.expiryDate?new Date((row.expirydate||row.expiryDate).split('/').reverse().join('-')).toISOString():null,
          allowedStartTime:row.allowedstarttime||row.allowedStartTime||'',
          allowedEndTime:row.allowedendtime||row.allowedEndTime||'',
          active:true
        });
        done++;
        setImportDone(done);
      }catch{}
    }
    await load();
    setImporting(false);
    setCsvRows([]);
    setImportAlert({type:'success',msg:`✅ Successfully imported ${done} tenant(s).`});
  };

  const filtered = tenants.filter(t=>{
    if(search && !t.name?.toLowerCase().includes(search.toLowerCase()) && !t.plates?.some(p=>p.toLowerCase().includes(search.toLowerCase()))) return false;
    if(filter==='active') return !isExpired(t.expiryDate);
    if(filter==='expired') return isExpired(t.expiryDate);
    if(filter==='expiring') return !isExpired(t.expiryDate) && daysLeft(t.expiryDate)<=7;
    return true;
  });

  const fmt = n => n?`AED ${Number(n).toFixed(0)}`:'—';
  const isNightOrDay = f => f==='monthly-night'||f==='monthly-day';

  return (
    <div className="fade-in">
      <div className="page-header">
        <div><div className="page-title">👥 Tenants</div><div className="page-subtitle">{tenants.length} registered · {tenants.filter(t=>!isExpired(t.expiryDate)).length} active</div></div>
        <div className="flex-row">
          <button className="btn btn-ghost btn-sm" onClick={load}><RefreshCw size={14}/></button>
          <button className="btn btn-outline btn-sm" onClick={exportToExcel}><Download size={14}/> Export</button>
          <button className="btn btn-primary btn-sm" onClick={openAdd}><Plus size={14}/> Add</button>
        </div>
      </div>
      <div className="page-body">

        {/* TABS */}
        <div style={{display:'flex',gap:0,borderBottom:'1px solid var(--border)',marginBottom:14}}>
          {[['list','👥 Tenants'],['import','📥 Bulk Import']].map(([k,l])=>(
            <button key={k} onClick={()=>setTab(k)} style={{padding:'10px 16px',background:'none',border:'none',cursor:'pointer',fontWeight:600,fontSize:14,color:tab===k?'var(--accent)':'var(--text-muted)',borderBottom:`2px solid ${tab===k?'var(--accent)':'transparent'}`,marginBottom:-1}}>
              {l}
            </button>
          ))}
        </div>

        {/* ── TENANT LIST TAB ── */}
        {tab==='list'&&<>
          {/* Filters */}
          <div style={{display:'flex',gap:8,marginBottom:12,flexWrap:'wrap'}}>
            <div style={{position:'relative',flex:1,minWidth:160}}>
              <Search size={14} style={{position:'absolute',left:10,top:'50%',transform:'translateY(-50%)',color:'var(--text-muted)'}}/>
              <input className="form-input" style={{paddingLeft:32,fontSize:14}} placeholder="Search name or plate..." value={search} onChange={e=>setSearch(e.target.value)}/>
            </div>
            {['all','active','expiring','expired'].map(f=>(
              <button key={f} onClick={()=>setFilter(f)} className={`btn btn-sm ${filter===f?'btn-primary':'btn-outline'}`} style={{textTransform:'capitalize'}}>{f}</button>
            ))}
          </div>

          {filtered.length===0
            ?<div className="empty-state"><div className="empty-state-icon">👥</div><div className="empty-state-text">No tenants found</div><button className="btn btn-primary" style={{marginTop:12}} onClick={openAdd}>+ Add First Tenant</button></div>
            :<div style={{display:'flex',flexDirection:'column',gap:10}}>
              {filtered.map(t=>{
                const dl = daysLeft(t.expiryDate);
                const exp = isExpired(t.expiryDate);
                const warn = !exp && dl<=7;
                return (
                  <div key={t.id} className="card" style={{borderLeft:`4px solid ${exp?'var(--danger)':warn?'var(--warning)':'var(--success)'}`,padding:'14px 16px'}}>
                    <div className="flex-between" style={{marginBottom:8}}>
                      <div style={{flex:1}}>
                        <div style={{fontWeight:700,fontSize:16}}>{t.name}</div>
                        <div style={{fontSize:13,color:'var(--text-muted)',marginTop:2}}>{getPkgLabel(t.packageType)}</div>
                      </div>
                      <div style={{display:'flex',gap:6,flexShrink:0}}>
                        <button className="btn btn-ghost btn-sm" onClick={()=>sendReminder(t)} title="WhatsApp Reminder"><Bell size={14}/></button>
                        <button className="btn btn-ghost btn-sm" onClick={()=>openEdit(t)}><Edit2 size={14}/></button>
                        <button className="btn btn-ghost btn-sm" style={{color:'var(--danger)'}} onClick={()=>openDelete(t)}><Trash2 size={14}/></button>
                      </div>
                    </div>
                    <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,fontSize:13}}>
                      <div><span style={{color:'var(--text-muted)'}}>Plates: </span><span style={{fontFamily:'var(--font-mono)',color:'var(--accent)',fontWeight:600}}>{t.plates?.join(', ')||'—'}</span></div>
                      <div><span style={{color:'var(--text-muted)'}}>Phone: </span><span>{t.phone||'—'}</span></div>
                      <div><span style={{color:'var(--text-muted)'}}>Rate: </span><span>{fmt(t.monthlyRate||t.weeklyRate||t.biweeklyRate)}</span></div>
                      <div>
                        <span style={{color:'var(--text-muted)'}}>Expiry: </span>
                        <span style={{color:exp?'var(--danger)':warn?'var(--warning)':'var(--text-primary)',fontWeight:600}}>
                          {fmtDate(t.expiryDate)} {exp?'⛔ EXPIRED':warn?`⚠️ ${dl}d left`:''}
                        </span>
                      </div>
                      {isNightOrDay(t.packageType)&&<div style={{gridColumn:'1/-1'}}><span style={{color:'var(--text-muted)'}}>Hours: </span><span style={{fontFamily:'var(--font-mono)'}}>{t.allowedStartTime}–{t.allowedEndTime}</span></div>}
                    </div>
                    <div style={{marginTop:10,display:'flex',gap:8}}>
                      <button className="btn btn-warning btn-sm" style={{flex:1}} onClick={()=>openRenew(t)}>🔄 Renew</button>
                      {exp&&<span className="badge badge-danger">EXPIRED</span>}
                      {warn&&!exp&&<span className="badge badge-warning">EXPIRING SOON</span>}
                      {!exp&&!warn&&<span className="badge badge-success">ACTIVE</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          }
        </>}

        {/* ── BULK IMPORT TAB ── */}
        {tab==='import'&&<>
          {importAlert&&<div className={`alert alert-${importAlert.type}`}>{importAlert.msg}</div>}

          <div className="card" style={{marginBottom:14}}>
            <div className="card-title">Step 1 — Download Template</div>
            <div style={{fontSize:14,color:'var(--text-muted)',marginBottom:12,lineHeight:1.6}}>
              Download the CSV template, fill in your tenant data in Excel or any spreadsheet app, then upload it below.
            </div>
            <div style={{background:'var(--bg-input)',borderRadius:'var(--radius-sm)',padding:'12px 14px',marginBottom:14,fontSize:13,fontFamily:'var(--font-mono)',color:'var(--text-secondary)'}}>
              <div style={{marginBottom:4,color:'var(--text-muted)',fontSize:11}}>COLUMNS:</div>
              <div>name, phone, plates, packageType, monthlyRate, weeklyRate, biweeklyRate, expiryDate, allowedStartTime, allowedEndTime</div>
              <div style={{marginTop:8,color:'var(--text-muted)',fontSize:11}}>PACKAGE TYPES:</div>
              <div>monthly-24hr · monthly-night · monthly-day · weekly · biweekly</div>
              <div style={{marginTop:8,color:'var(--text-muted)',fontSize:11}}>NOTES:</div>
              <div>• Multiple plates: separate with semicolon (28031;70046)</div>
              <div>• Date format: DD/MM/YYYY</div>
              <div>• Night/Day packages: fill allowedStartTime and allowedEndTime (e.g. 22:00)</div>
            </div>
            <button className="btn btn-primary btn-full" onClick={downloadTemplate}>
              <Download size={16}/> Download CSV Template
            </button>
          </div>

          <div className="card" style={{marginBottom:14}}>
            <div className="card-title">Step 2 — Upload Your CSV</div>
            <input ref={fileRef} type="file" accept=".csv" style={{display:'none'}} onChange={handleFileUpload}/>
            <button className="btn btn-outline btn-full btn-lg" onClick={()=>fileRef.current.click()}>
              <Upload size={18}/> Choose CSV File
            </button>
          </div>

          {csvRows.length>0&&(
            <div className="card">
              <div className="flex-between" style={{marginBottom:12}}>
                <div className="card-title" style={{margin:0}}>Step 3 — Review & Import ({csvRows.length} rows)</div>
                <button className="btn btn-success" onClick={handleImportAll} disabled={importing}>
                  {importing?`Importing ${importDone}/${csvRows.filter(r=>r.valid).length}...`:`✅ Import All (${csvRows.filter(r=>r.valid).length})`}
                </button>
              </div>
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr><th>Name</th><th>Phone</th><th>Plates</th><th>Package</th><th>Rate</th><th>Expiry</th><th>Status</th></tr>
                  </thead>
                  <tbody>
                    {csvRows.map((row,i)=>(
                      <tr key={i} style={{background:row.valid?'':' var(--danger-dim)'}}>
                        <td style={{fontWeight:600}}>{row.name||'—'}</td>
                        <td style={{fontSize:12}}>{row.phone||'—'}</td>
                        <td style={{fontFamily:'var(--font-mono)',color:'var(--accent)',fontSize:12}}>{row.plates?.join(', ')||'—'}</td>
                        <td><span className="badge badge-neutral" style={{fontSize:10}}>{row.packagetype||row.packageType||'—'}</span></td>
                        <td style={{fontFamily:'var(--font-mono)',fontSize:12}}>{row.monthlyrate||row.monthlyRate||row.weeklyrate||row.weeklyRate||'—'}</td>
                        <td style={{fontSize:12}}>{row.expirydate||row.expiryDate||'—'}</td>
                        <td>{row.valid?<span className="badge badge-success">✓ OK</span>:<span className="badge badge-danger" title={row.error}>✗ Error</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>}
      </div>

      {/* ADD/EDIT MODAL */}
      {(modal==='add'||modal==='edit')&&(
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <div className="modal-title">{modal==='add'?'➕ Add Tenant':'✏️ Edit Tenant'}</div>
              <button className="btn btn-ghost btn-sm" onClick={closeModal}>✕</button>
            </div>
            <div className="modal-body" style={{maxHeight:'70vh',overflowY:'auto'}}>
              {alert&&<div className={`alert alert-${alert.type}`}>{alert.msg}</div>}
              <div className="form-group"><label className="form-label">Full Name *</label><input className="form-input" value={form.name} onChange={e=>setForm({...form,name:e.target.value})} placeholder="Customer name"/></div>
              <div className="form-group"><label className="form-label">Phone (WhatsApp)</label><input className="form-input" type="tel" value={form.phone} onChange={e=>setForm({...form,phone:e.target.value})} placeholder="+971 50 000 0000"/></div>
              <div className="form-group">
                <label className="form-label">Plate Numbers</label>
                {form.plates.map((p,i)=>(
                  <div key={i} className="flex-row" style={{marginBottom:6}}>
                    <input className="form-input" style={{fontFamily:'var(--font-mono)',fontWeight:700,flex:1}} value={p} onChange={e=>{const pl=[...form.plates];pl[i]=e.target.value.toUpperCase();setForm({...form,plates:pl});}} placeholder="e.g. 28031"/>
                    {form.plates.length>1&&<button className="btn btn-ghost btn-sm" style={{color:'var(--danger)'}} onClick={()=>setForm({...form,plates:form.plates.filter((_,idx)=>idx!==i)})}>✕</button>}
                  </div>
                ))}
                <button className="btn btn-outline btn-sm" onClick={()=>setForm({...form,plates:[...form.plates,'']})}>+ Add Plate</button>
              </div>
              <div className="form-group"><label className="form-label">Package Type</label>
                <select className="form-select" value={form.packageType} onChange={e=>setForm({...form,packageType:e.target.value})}>
                  {PKG_TYPES.map(p=><option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
              </div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
                <div className="form-group"><label className="form-label">Monthly Rate (AED)</label><input className="form-input" type="number" value={form.monthlyRate} onChange={e=>setForm({...form,monthlyRate:e.target.value})} placeholder="e.g. 500"/></div>
                <div className="form-group"><label className="form-label">Weekly Rate (AED)</label><input className="form-input" type="number" value={form.weeklyRate} onChange={e=>setForm({...form,weeklyRate:e.target.value})} placeholder="e.g. 150"/></div>
              </div>
              {isNightOrDay(form.packageType)&&(
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
                  <div className="form-group"><label className="form-label">Allowed From</label><input className="form-input" type="time" value={form.allowedStartTime} onChange={e=>setForm({...form,allowedStartTime:e.target.value})}/></div>
                  <div className="form-group"><label className="form-label">Allowed Until</label><input className="form-input" type="time" value={form.allowedEndTime} onChange={e=>setForm({...form,allowedEndTime:e.target.value})}/></div>
                </div>
              )}
              <div className="form-group"><label className="form-label">Expiry Date</label><input className="form-input" type="date" value={form.expiryDate} onChange={e=>setForm({...form,expiryDate:e.target.value})}/></div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={closeModal}>Cancel</button>
              <button className="btn btn-success" onClick={modal==='add'?handleCreate:handleEdit}>{modal==='add'?'✅ Add Tenant':'✅ Save Changes'}</button>
            </div>
          </div>
        </div>
      )}

      {/* RENEW MODAL */}
      {modal==='renew'&&selected&&(
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header"><div className="modal-title">🔄 Renew Package</div><button className="btn btn-ghost btn-sm" onClick={closeModal}>✕</button></div>
            <div className="modal-body">
              {alert&&<div className={`alert alert-${alert.type}`}>{alert.msg}</div>}
              <div style={{background:'var(--bg-input)',padding:'12px',borderRadius:'var(--radius-sm)',marginBottom:14}}>
                <div style={{fontWeight:700,fontSize:16}}>{selected.name}</div>
                <div style={{fontSize:13,color:'var(--text-muted)',marginTop:4}}>{getPkgLabel(selected.packageType)}</div>
                <div style={{fontSize:13,color:isExpired(selected.expiryDate)?'var(--danger)':'var(--warning)',marginTop:4}}>
                  Current expiry: {fmtDate(selected.expiryDate)}
                </div>
              </div>
              <div className="form-group"><label className="form-label">Package Type (for this renewal)</label>
                <select className="form-select" value={renewPkgType||selected.packageType} onChange={e=>setRenewPkgType(e.target.value)}>
                  {PKG_TYPES.map(p=><option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
              </div>
              <div className="form-group"><label className="form-label">New Expiry Date *</label>
                <input className="form-input" type="date" min={new Date(Date.now()+86400000).toISOString().split('T')[0]} value={renewExpiryDate} onChange={e=>setRenewExpiryDate(e.target.value)}/>
                {renewExpiryDate&&<div style={{fontSize:12,color:'var(--success)',marginTop:4}}>New expiry: {fmtDate(renewExpiryDate)}</div>}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={closeModal}>Cancel</button>
              <button className="btn btn-success" onClick={handleRenew}>✅ Renew</button>
            </div>
          </div>
        </div>
      )}

      {/* DELETE MODAL */}
      {modal==='delete'&&selected&&(
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header"><div className="modal-title">🗑️ Delete Tenant</div><button className="btn btn-ghost btn-sm" onClick={closeModal}>✕</button></div>
            <div className="modal-body">
              <div className="alert alert-danger">This will permanently delete <strong>{selected.name}</strong> and all their records.</div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={closeModal}>Cancel</button>
              <button className="btn btn-danger" onClick={handleDelete}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
